import React, { ReactNode } from 'react';
import reactStringReplace from 'react-string-replace';

/**
 * This helper function is aware of GraphQL errors and attempts to log all of the important information about them to
 * the console.
 *
 * @param error - The error to be logged, which might be a GraphQL error.
 */
export function logGraphQLError( error: any ) {
  console.group( 'API Error' );

  if ( error.message && error.message.length ) {
    console.error( 'Generic error:', error.message );
  }

  if ( error.errors && error.errors.length ) {
    console.error(
      'Generic errors:',
      error.errors.map( ( apiError: any ) => apiError.message || apiError ),
    );
  }

  if ( error.graphQLErrors && error.graphQLErrors.length ) {
    console.error(
      'GraphQL errors:',
      error.graphQLErrors.map( ( graphQLError: any ) => graphQLError.message || graphQLError ),
    );
  }

  if ( error.clientErrors && error.clientErrors.length ) {
    console.error(
      'Client errors:',
      error.clientErrors.map( ( clientError: any ) => clientError.message || clientError ),
    );
  }

  if ( error.networkError ) {
    console.error( 'Network log:', error.networkError.message );
    ( error.networkError.result?.errors || [] ).forEach( ( networkError: any ) => console.error( networkError.message ) );
  }

  console.groupEnd();
}

/**
 * The real ApolloError type definition sometimes does not match what Apollo gives us
 */
export type BasicApolloError = {
  message: string;
  graphQLErrors?: Array<any>;
  clientErrors?: Array<any>;
  networkError?: Record<string, any>;
};
export type GraphQLDataObject = Record<string, any> & { errors?: Array<Record<string, any>> };
export type GraphQLErrorMessageCategories = 'generic' | 'specific' | 'graphQL' | 'client' | 'network' | 'api' | 'all';
export type Replacement = { from: string | RegExp; to: string };

/**
 * @typedef GraphQLErrorTypes
 * @property {string} generic "top-level message property"
 * @property {string} specific - NOT generic (graphQL, client, and network)
 * @property {string} graphQL
 * @property {string} client
 * @property {string} network
 * @property {string} all - specific AND generic.
 */
/**
 * Extract strings from an Apollo GraphQL error for displaying in the UI or logging.
 *
 * @param error - The GraphQL error, from e.g. const { error } = useQuery().
 * @param data - The data returned by the API, which may have an errors property. Required if
 * include param includes “api”.
 * @param limit - If there are multiple error messages, how many of them to display.
 * @param {GraphQLErrorTypes} include - Which types of errors to get.
 *
 * TODO: Break this up into multiple functions: get messages for Error, for GQL Client error, for
 * GQL Network error, for API error, etc.
 */
export function getGraphQLErrorMessages(
  // Should have been ApolloError but the type is not accurate:
  error: any,
  data: GraphQLDataObject | null = null,
  include: GraphQLErrorMessageCategories[] = ['all'],
  limit: number = Infinity,
  substitutions: Replacement[] = [],
) {
  error as BasicApolloError;
  let messages: string[] = [];

  if ( error ) {
    const { message, graphQLErrors, clientErrors, networkError } = error;

    if ( include.includes( 'all' ) || include.includes( 'generic' ) ) {
      if ( message ) {
        messages = messages.concat( Array.from( new Set( message.split( '\n' ) ) ) );
      }
    }

    if ( include.includes( 'all' ) || include.includes( 'specific' ) || include.includes( 'graphQL' ) ) {
      if ( graphQLErrors && graphQLErrors.length ) {
        if ( limit === Infinity ) {
          messages = messages.concat(
            graphQLErrors.map( ( graphQLError: Record<string, any> & { path?: string[] } ) => {
              let graphQLErrorMessage = `${graphQLError.message.replace( /\.$/, '' )}`;

              // TODO: This is not the best way to do this. We can improve the structure coming from the backend
              // so we don't have to do so much in the front-end.
              if (
                graphQLError.extensions &&
                graphQLError.extensions.code &&
                graphQLError.extensions.code &&
                graphQLError.extensions.code === 'INPUT_FIELDS_ERROR' &&
                graphQLError.extensions.errors
              ) {
                graphQLErrorMessage = graphQLError.extensions.errors.map(
                  ( fieldError: { key: string; message: string } ) => fieldError.message,
                );
              }

              // `path` usually exists, but may not in unit tests
              if ( graphQLError.path && graphQLError.path.length ) {
                graphQLErrorMessage += ` for query path ${graphQLError.path.join( '.' )}`;
              }

              return graphQLErrorMessage;
            } ),
          );
        } else {
          const graphQLError = graphQLErrors[limit - 1];

          if ( graphQLError?.message ) {
            let graphQLErrorMessage = `${graphQLError.message.replace( /\.$/, '' )}`;

            // `path` usually exists, but may not in unit tests
            if ( graphQLError.path && graphQLError.path.length ) {
              graphQLErrorMessage += ` for query path ${graphQLError.path.join( '.' )}`;
            }

            messages = messages.concat( [graphQLErrorMessage] );
          }
        }
      }
    }

    if ( include.includes( 'all' ) || include.includes( 'specific' ) || include.includes( 'client' ) ) {
      if ( clientErrors && clientErrors.length ) {
        if ( limit === Infinity ) {
          messages = messages.concat( clientErrors.map( ( clientError: Record<string, any> ) => clientError.message ) );
        } else {
          messages = messages.concat( [clientErrors[limit - 1].message] );
        }
      }
    }

    if ( include.includes( 'all' ) || include.includes( 'specific' ) || include.includes( 'network' ) ) {
      if ( networkError && networkError.result?.errors ) {
        messages = messages.concat( networkError.result.errors.map( ( netError: Record<string, any> ) => netError.message ) );
      }
    }
  }

  if ( data ) {
    if ( include.includes( 'all' ) || include.includes( 'specific' ) || include.includes( 'api' ) ) {
      const keys = Object.keys( data );

      if ( keys.length ) {
        /**
         * The first property of `data` could be literally anything depending on the endpoint,
         * e.g. `createUser`. So just blindly grab the first one and look for any errors as
         * a subproperty of that one.
         */
        const firstKey = keys[0];

        if ( data[firstKey] && data[firstKey].errors && data[firstKey].errors.length ) {
          messages = messages.concat( data[firstKey].errors.map( ( apiError: Record<string, string> ) => apiError.message ) );
        }
      }
    }
  }

  if ( substitutions?.length ) {
    substitutions.forEach( ( { from, to } ) => {
      messages = messages.map( ( message ) => message.replace( from, to ) );
    } );
  }

  return messages;
}

/**
 * Extract strings from an Apollo GraphQL error and transform them into React nodes for displaying to an end user.
 *
 * @param error - The GraphQL error, from e.g. const { error } = useQuery().
 * @param data - The data returned by the API, which may have an errors property. Required if include param includes “api”.
 * @param limit - If there are multiple error messages, how many of them to display.
 * @param {GraphQLErrorTypes} include - Which types of errors to get.
 */
export function getGraphQLErrorMessagesUi(
  error: any,
  data: GraphQLDataObject | null = null,
  include: GraphQLErrorMessageCategories[] = ['specific'],
  limit: number = Infinity,
  substitutions: Replacement[] = [],
  mode: 'list' | 'paragraphs' = 'paragraphs',
): ReactNode[] {
  const graphQLErrorMessages = getGraphQLErrorMessages( error, data, include, limit, substitutions ).map(
    ( message, index ) => {
      // Only works for English:
      const messageEndsInPunctuation = ['.', '?', '!', '‽'].includes( message[message.length - 1] );

      /**
       * Replace newlines with <br /> tags for readabililty.
       */
      const replaceNewlines = reactStringReplace( message, /([^\n]+)\n/g, ( match ) => (
        <>
          { match } <br />
        </>
      ) );

      /*
      Typography adjustments to GraphQL error messages.

      Wraps matching text in `<code />` JSX nodes.
      Each call to `reactStringReplace` returns a
      `ReactNodeArray` which can be fed back into
      `reactStringReplace` as input for further replacements,
      so we can stack them. Throw the last one in the stack
      into the returned `<p />`.
    */
      /**
       * Replace straight double quotes with <code /> (TODO: example)
       */
      const doubleQuotesToCode = reactStringReplace( replaceNewlines, /"([^"]+)"/g, ( match, matchIndex ) => (
        <code key={ `double-quotes-to-code-${matchIndex}` }>{ match }</code>
      ) );
      /**
     * Wrap words immediately following the word “field” with <code />, e.g.
       “Cannot return null for non-nullable field Station.blipCount”.
       becomes
       “Cannot return null for non-nullable field <code>Station.blipCount</code>”
     */

      const keyWordsToCode = reactStringReplace( doubleQuotesToCode, /return (\bnull\b)/g, ( match, matchIndex ) => (
        <React.Fragment key={ `key-words-to-code-${matchIndex}` }>
          return <code>{ match }</code>
        </React.Fragment>
      ) );
      const queryPathToCode = reactStringReplace( keyWordsToCode, /for query path (\b\S+\b)/g, ( match, matchIndex ) => (
        <React.Fragment key={ `query-path-to-code-${matchIndex}` }>
          for query path <code>{ match }</code>
        </React.Fragment>
      ) );
      const textFollowingTheWordFieldToCode = reactStringReplace(
        queryPathToCode,
        /field (\b\S+\b)/g,
        ( match, matchIndex ) => (
          <React.Fragment key={ `text-following-the-word-field-to-code-${matchIndex}` }>
            field <code>{ match }</code>
          </React.Fragment>
        ),
      );

      const messageUi = (
        <>
          { textFollowingTheWordFieldToCode }
          { !messageEndsInPunctuation && '.' }
        </>
      );

      return mode === 'list' ? <li key={ index }>{ messageUi }</li> : <p key={ index }>{ messageUi }</p>;
    },
  );

  if ( mode === 'list' ) {
    // The key here is required because this is a list of components. It's not important because it
    // is a list of 1 item.
    return [
      <ul key={ 0 } style={ { paddingLeft: '1rem', textAlign: 'left' } }>
        { graphQLErrorMessages }
      </ul>,
    ];
  }

  return graphQLErrorMessages;
}
